
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CloudUpload, Download, FileText, Check, X } from "lucide-react";

const BulkUploadTab: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<"idle" | "validating" | "error" | "ready">("idle");
  const [previewData, setPreviewData] = useState<any[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadStatus("validating");
      
      // Simulate validation process
      setTimeout(() => {
        // Mock preview data
        const mockData = [
          {
            name: "Organic Apple Juice",
            sku: "BEVR-001",
            barcode: "8901234567800",
            category: "Beverages",
            brand: "Nature's Best",
            packSize: "1L",
            purchasePrice: 1.50,
            sellingPricePOS: 2.99,
            sellingPriceJimatShop: 2.79,
            stock: 25,
            lowStockThreshold: 10,
            status: "valid"
          },
          {
            name: "Whole Wheat Bread",
            sku: "BAKE-012",
            barcode: "8901234567801",
            category: "Bakery",
            brand: "Daily Fresh",
            packSize: "400g",
            purchasePrice: 0.90,
            sellingPricePOS: 1.89,
            sellingPriceJimatShop: 1.79,
            stock: 15,
            lowStockThreshold: 5,
            status: "valid"
          },
          {
            name: "Natural Yogurt",
            sku: "",  // Missing SKU - error
            barcode: "8901234567802",
            category: "Dairy",
            brand: "Fresh Farm",
            packSize: "500g",
            purchasePrice: 1.20,
            sellingPricePOS: 2.49,
            sellingPriceJimatShop: 2.39,
            stock: 5,
            lowStockThreshold: 10,
            status: "error"
          },
          {
            name: "Chocolate Cookies",
            sku: "SNCK-023",
            barcode: "8901234567803",
            category: "Snacks",
            brand: "Sweet Treats",
            packSize: "200g",
            purchasePrice: 1.10,
            sellingPricePOS: 2.29,
            sellingPriceJimatShop: 2.19,
            stock: 0,
            lowStockThreshold: 5,
            status: "valid"
          },
        ];
        
        setPreviewData(mockData);
        
        // Check if there are any errors
        const hasErrors = mockData.some(item => item.status === "error");
        setUploadStatus(hasErrors ? "error" : "ready");
      }, 1500);
    }
  };

  const handleImport = () => {
    console.log("Importing products from file:", file?.name);
    // Here you would typically process the file and import the products
    
    // Clear the form after successful import
    setTimeout(() => {
      setFile(null);
      setPreviewData([]);
      setUploadStatus("idle");
      alert("Products imported successfully!");
    }, 1000);
  };

  // Get stock status with color indication
  const getStockStatus = (stock: number, lowStockThreshold: number) => {
    if (stock <= 0) {
      return { text: "Out of Stock", color: "text-red-600" };
    } else if (stock < lowStockThreshold) {
      return { text: `Low Stock (${stock})`, color: "text-amber-600" };
    } else {
      return { text: `In Stock (${stock})`, color: "text-green-600" };
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Bulk Upload Products</h2>
        <p className="text-muted-foreground">
          Upload multiple products at once using a CSV or Excel file.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Step 1: Download Template</h3>
              <p className="text-sm text-muted-foreground">
                Download our template file and fill in your product details. Make sure to include stock quantities and low stock thresholds.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                CSV Template
              </Button>
              <Button variant="outline" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Excel Template
              </Button>
            </div>
          </div>

          <div className="border-t my-6" />

          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Step 2: Upload Your File</h3>
              <p className="text-sm text-muted-foreground">
                Upload your completed file to import products.
              </p>
            </div>
            
            <div className="border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center">
              {!file ? (
                <>
                  <CloudUpload className="h-12 w-12 text-muted-foreground mb-2" />
                  <p className="text-center text-muted-foreground mb-4">
                    Drag and drop your file here or click to browse
                  </p>
                  <Input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={handleFileChange}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById("file-upload")?.click()}
                    variant="outline"
                  >
                    Browse Files
                  </Button>
                </>
              ) : (
                <div className="w-full space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-6 w-6 text-blue-500" />
                      <div>
                        <p className="font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setFile(null);
                        setPreviewData([]);
                        setUploadStatus("idle");
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {uploadStatus === "validating" && (
                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                      <div className="animate-spin h-4 w-4 border-2 border-t-transparent border-blue-500 rounded-full"></div>
                      Validating file...
                    </div>
                  )}
                  
                  {uploadStatus === "error" && (
                    <div className="text-sm text-red-500 flex items-center gap-2">
                      <X className="h-4 w-4" />
                      Errors found in file. Please fix them before importing.
                    </div>
                  )}
                  
                  {uploadStatus === "ready" && (
                    <div className="text-sm text-green-500 flex items-center gap-2">
                      <Check className="h-4 w-4" />
                      File validated successfully! Ready to import.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Data Preview</h3>
          
          {previewData.length > 0 ? (
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product Name</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Stock</TableHead>
                    <TableHead>Low Stock</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.map((item, index) => {
                    const stockStatus = getStockStatus(item.stock, item.lowStockThreshold);
                    return (
                      <TableRow key={index} className={item.status === "error" ? "bg-red-50" : ""}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>
                          {item.sku || (
                            <span className="text-red-500">Missing</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={stockStatus.color}>{stockStatus.text}</span>
                        </TableCell>
                        <TableCell>{item.lowStockThreshold}</TableCell>
                        <TableCell>
                          {item.status === "valid" ? (
                            <span className="text-green-500 flex items-center gap-1">
                              <Check className="h-4 w-4" /> Valid
                            </span>
                          ) : (
                            <span className="text-red-500 flex items-center gap-1">
                              <X className="h-4 w-4" /> Error
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="border rounded-md p-8 flex items-center justify-center">
              <p className="text-muted-foreground">
                Upload a file to preview data
              </p>
            </div>
          )}
          
          {previewData.length > 0 && (
            <Button 
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={uploadStatus !== "ready"}
              onClick={handleImport}
            >
              <Check className="mr-2 h-4 w-4" />
              Import {previewData.filter(item => item.status === "valid").length} Products
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadTab;
